import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Density-Modi:
 * - `'comfortable'` (default): grosszuegiges Padding (`--density-y = 1`).
 * - `'compact'`: dichteres Padding (`--density-y = 0.85`).
 *
 * Wirkung: Card-Primitive nutzt `var(--density-y)` als Multiplikator fuer das
 * vertikale Padding. Subtile Aenderung — keine drastische UI-Verschiebung.
 *
 * Persistiert in localStorage (`civiport-density`).
 */
export type Density = 'comfortable' | 'compact';

const STORAGE_KEY = 'civiport-density';

const DENSITY_VALUES: Record<Density, string> = {
  comfortable: '1',
  compact: '0.85',
};

interface DensityContextValue {
  density: Density;
  setDensity: (d: Density) => void;
}

const DensityContext = createContext<DensityContextValue | null>(null);

function readSavedDensity(): Density {
  if (typeof window === 'undefined') return 'comfortable';
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'comfortable' || saved === 'compact') return saved;
  } catch {
    /* localStorage kann blockiert sein */
  }
  return 'comfortable';
}

function applyDensity(d: Density): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--density-y', DENSITY_VALUES[d]);
  document.documentElement.dataset.density = d;
}

interface DensityProviderProps {
  children: ReactNode;
}

export function DensityProvider({ children }: DensityProviderProps): JSX.Element {
  const [density, setDensityState] = useState<Density>(() => readSavedDensity());

  useEffect(() => {
    applyDensity(density);
  }, [density]);

  const setDensity = useCallback((d: Density) => {
    setDensityState(d);
    try {
      window.localStorage.setItem(STORAGE_KEY, d);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<DensityContextValue>(
    () => ({ density, setDensity }),
    [density, setDensity],
  );

  return <DensityContext.Provider value={value}>{children}</DensityContext.Provider>;
}

export function useDensity(): DensityContextValue {
  const ctx = useContext(DensityContext);
  if (!ctx) {
    throw new Error('useDensity muss innerhalb von <DensityProvider> verwendet werden.');
  }
  return ctx;
}
